import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Eye, Loader2, Search, Tag, Trash2, XCircle } from "lucide-react";
import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import CouponConditionFields, { buildCouponConditionPayload, validateCouponCondition } from "../../components/Coupons/CouponConditionFields";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Input,
  PageHeader,
  Select,
  StatusPill,
  Switch,
  TableShell,
  Textarea,
  formatCurrency,
  getErrorMessage,
} from "./components/FoodAdminUi";

const blankForm = {
  code: "",
  title: "",
  description: "",
  discountType: "PERCENT",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "0",
  usageLimit: "",
  perUserLimit: "1",
  restaurantId: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
  conditionType: "NONE",
  firstOrderOnly: false,
  validDays: [],
  validFromTime: "",
  validToTime: "",
  allowedCities: "",
  allowedStates: "",
  allowedPincodes: "",
};

function unwrapList(res) {
  return rowsOf(res?.data || res);
}

function getItems(res) {
  const root = res?.data || res || {};
  if (Array.isArray(root.items)) return root.items;
  if (Array.isArray(root.coupons)) return root.coupons;
  if (Array.isArray(root.data?.items)) return root.data.items;
  return rowsOf(root);
}

function getMeta(res) {
  const root = res?.data || res || {};
  return root.meta || root.data?.meta || { total: getItems(res).length };
}

function money(value) {
  return formatCurrency(Number(value || 0));
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function couponLabel(coupon) {
  const type = String(coupon.discountType || coupon.discount_type || "").toUpperCase();
  const value = Number(coupon.discountValue ?? coupon.discount_value ?? 0);
  const cap = coupon.maxDiscountAmount ?? coupon.max_discount_amount;
  if (type === "PERCENT") return `${value}% off${cap ? ` up to ${money(cap)}` : ""}`;
  return `${money(value)} off`;
}

const DAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

function rulesOf(item = {}) {
  const rules = item.specialRules || item.special_rules || item.metadata?.rules || item.rules || {};
  return {
    firstOrderOnly: Boolean(rules.firstOrderOnly || rules.first_order_only),
    validDays: Array.isArray(rules.validDays || rules.valid_days) ? (rules.validDays || rules.valid_days).map(Number) : [],
    validFromTime: rules.validFromTime || rules.valid_from_time || "",
    validToTime: rules.validToTime || rules.valid_to_time || "",
    cities: rules.cities || rules.allowedCities || rules.allowed_cities || [],
    states: rules.states || rules.allowedStates || rules.allowed_states || [],
    pincodes: rules.pincodes || rules.allowedPincodes || rules.allowed_pincodes || [],
  };
}

function formatRules(item = {}) {
  const rules = rulesOf(item);
  const parts = [];
  if (rules.firstOrderOnly) parts.push("First order only");
  if (rules.validDays.length) {
    const labels = rules.validDays.map((day) => DAY_OPTIONS.find((d) => d.value === Number(day))?.label).filter(Boolean);
    if (labels.length) parts.push(`Days: ${labels.join(", ")}`);
  }
  if (rules.validFromTime || rules.validToTime) parts.push(`Time: ${rules.validFromTime || "00:00"}-${rules.validToTime || "23:59"}`);
  if (rules.cities?.length) parts.push(`Cities: ${rules.cities.join(", ")}`);
  if (rules.states?.length) parts.push(`States: ${rules.states.join(", ")}`);
  if (rules.pincodes?.length) parts.push(`Pincodes: ${rules.pincodes.join(", ")}`);
  return parts.join(" · ") || "No special rules";
}

function normalizePayload(form) {
  const payload = {
    code: String(form.code || "").trim().toUpperCase(),
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim() || null,
    discountType: form.discountType,
    discountValue: Number(form.discountValue || 0),
    maxDiscountAmount: form.maxDiscountAmount === "" ? null : Number(form.maxDiscountAmount || 0),
    minOrderAmount: Number(form.minOrderAmount || 0),
    usageLimit: form.usageLimit === "" ? null : Number(form.usageLimit || 0),
    perUserLimit: Number(form.perUserLimit || 1),
    restaurantId: form.restaurantId || null,
    startsAt: form.startsAt || null,
    endsAt: form.endsAt || null,
    isActive: Boolean(form.isActive),
    ...buildCouponConditionPayload(form),
  };
  if (payload.discountType === "FIXED") payload.maxDiscountAmount = null;
  return payload;
}


function usageStatusClass(status) {
  const v = String(status || "").toUpperCase();
  if (v === "REDEEMED") return "bg-emerald-50 text-emerald-700";
  if (v === "RESERVED") return "bg-amber-50 text-amber-700";
  if (v === "RELEASED") return "bg-slate-50 text-slate-600";
  return "bg-slate-50 text-slate-600";
}

function normalizeUsage(item = {}) {
  const user = item.user || {};
  const order = item.order || {};
  return {
    id: item.id || `${item.orderId || order.id || "order"}-${item.appliedAt || item.usedAt || "usage"}`,
    userName: item.userName || user.name || "Unknown user",
    userPhone: item.userPhone || user.phone || "",
    userEmail: item.userEmail || user.email || "",
    orderNumber: item.orderNumber || order.number || "—",
    subtotal: Number(item.subtotal ?? order.subtotal ?? 0),
    couponDiscountAmount: Number(item.couponDiscountAmount ?? order.discountAmount ?? 0),
    grandTotal: Number(item.grandTotal ?? order.grandTotal ?? 0),
    status: item.status || item.redemptionStatus || item.redemption_status || "—",
    orderStatus: item.orderStatus || order.status || "—",
    paymentStatus: item.paymentStatus || order.paymentStatus || "—",
    itemCount: Number(item.itemCount ?? order.itemCount ?? 0),
    totalQty: Number(item.totalQty ?? order.totalQty ?? 0),
    addressText: item.addressText || order.addressText || "",
    usedAt: item.usedAt || item.appliedAt || item.createdAt || "",
  };
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function safeRestaurantLabel(coupon, restaurantNameById) {
  if (!coupon.restaurantId) return "All restaurants";
  const name = coupon.restaurantName || restaurantNameById.get(String(coupon.restaurantId));
  if (name && !isUuidLike(name)) return name;
  return "Specific restaurant";
}

export default function FoodCouponsPage() {
  const [items, setItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [filters, setFilters] = useState({ search: "", status: "", active: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [usageCoupon, setUsageCoupon] = useState(null);
  const [usageItems, setUsageItems] = useState([]);
  const [usageMeta, setUsageMeta] = useState({ page: 1, limit: 10, total: 0 });
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageSearch, setUsageSearch] = useState("");
  const [usageStatus, setUsageStatus] = useState("");
  const [usageLoading, setUsageLoading] = useState(false);

  const restaurantNameById = useMemo(() => {
    const map = new Map();
    restaurants.forEach((row) => {
      const id = row.id || row.restaurantId || row.restaurant_id;
      const name = row.name || row.restaurantName || row.restaurant_name || row.businessName || row.business_name || row.legalName || row.legal_name || "";
      if (id && name && !isUuidLike(name)) map.set(String(id), name);
    });
    return map;
  }, [restaurants]);

  const loadRestaurants = useCallback(async () => {
    try {
      const res = await restaurantAdminService.listRestaurants({ limit: 200 });
      setRestaurants(unwrapList(res));
    } catch {
      setRestaurants([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await restaurantAdminService.listRestaurantCoupons({ ...filters, limit: 50 });
      setItems(getItems(res));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadRestaurants(); }, [loadRestaurants]);
  useEffect(() => { load(); }, [load]);

  function patchForm(key, value) {
    setForm((current) => {
      if (key === "discountType") {
        return { ...current, discountType: value, maxDiscountAmount: value === "FIXED" ? "" : current.maxDiscountAmount };
      }
      if (key === "validDays") {
        if (Array.isArray(value)) {
          return { ...current, validDays: value.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6).sort((a, b) => a - b) };
        }
        const day = Number(value);
        const currentDays = Array.isArray(current.validDays) ? current.validDays : [];
        const validDays = currentDays.includes(day) ? currentDays.filter((item) => item !== day) : [...currentDays, day].sort((a, b) => a - b);
        return { ...current, validDays };
      }
      return { ...current, [key]: value };
    });
  }

  async function runAction(fn, okMessage) {
    setError("");
    setMessage("");
    try {
      await fn();
      setMessage(okMessage);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const conditionError = validateCouponCondition(form);
      if (conditionError) {
        setError(conditionError);
        return;
      }
      await restaurantAdminService.createRestaurantCoupon(normalizePayload(form));
      setForm(blankForm);
      setMessage("Restaurant coupon created.");
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleReject(id) {
    const reason = window.prompt("Reject reason?");
    if (!reason) return;
    await runAction(() => restaurantAdminService.rejectRestaurantCoupon(id, reason), "Coupon rejected.");
  }

  async function handleDelete(item) {
    const ok = window.confirm(`Delete coupon ${item.code}?`);
    if (!ok) return;
    await runAction(() => restaurantAdminService.deleteRestaurantCoupon(item.id), "Coupon deleted.");
  }

  async function loadCouponUsage(coupon = usageCoupon, nextPage = 1, overrides = {}) {
    if (!coupon?.id) return;
    setUsageLoading(true);
    setError("");
    try {
      const res = await restaurantAdminService.listRestaurantCouponUsages(coupon.id, {
        page: nextPage,
        limit: usageMeta.limit || 10,
        search: overrides.search ?? usageSearch,
        status: overrides.status ?? usageStatus,
      });
      setUsageItems(getItems(res).map(normalizeUsage));
      setUsageMeta(getMeta(res));
      setUsageSummary((res?.data || res || {}).summary || null);
    } catch (err) {
      setUsageItems([]);
      setUsageSummary(null);
      setError(getErrorMessage(err));
    } finally {
      setUsageLoading(false);
    }
  }

  async function toggleUsage(item) {
    if (usageCoupon?.id === item.id) {
      setUsageCoupon(null);
      setUsageItems([]);
      setUsageSummary(null);
      return;
    }
    setUsageCoupon(item);
    setUsageItems([]);
    setUsageSummary(null);
    setUsageStatus("");
    setUsageSearch("");
    await loadCouponUsage(item, 1, { status: "", search: "" });
  }

  async function applyUsageFilters() {
    await loadCouponUsage(usageCoupon, 1);
  }

  function renderUsagePanel(item) {
    if (usageCoupon?.id !== item.id) return null;
    const totalPages = Math.max(1, Math.ceil(Number(usageMeta.total || 0) / Number(usageMeta.limit || 10)));
    const totalRecords = usageSummary?.totalUsages ?? usageMeta.total ?? 0;

    return (
      <tr>
        <td colSpan={7} className="border-b border-[#f2c6cc] bg-white p-4">
          <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-black text-slate-950">Usage details — {item.code}</div>
                <div className="text-xs text-slate-500">Customer, order amount, coupon discount and paid amount.</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setUsageCoupon(null)}>Close</Button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <div className="rounded-2xl bg-[#fffafa] p-3">
                <div className="text-xs text-slate-500">Total records</div>
                <div className="text-lg font-black text-slate-950">{totalRecords}</div>
              </div>
              <div className="rounded-2xl bg-[#fffafa] p-3">
                <div className="text-xs text-slate-500">Redeemed</div>
                <div className="text-lg font-black text-slate-950">{usageSummary?.redeemedUsages ?? 0}</div>
              </div>
              <div className="rounded-2xl bg-[#fffafa] p-3">
                <div className="text-xs text-slate-500">Coupon discount</div>
                <div className="text-lg font-black text-slate-950">{money(usageSummary?.totalDiscountAmount || 0)}</div>
              </div>
              <div className="rounded-2xl bg-[#fffafa] p-3">
                <div className="text-xs text-slate-500">Paid amount</div>
                <div className="text-lg font-black text-slate-950">{money(usageSummary?.totalGrandTotal || 0)}</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <div className="relative min-w-[260px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={usageSearch}
                  onChange={(e) => setUsageSearch(e.target.value)}
                  placeholder="Search customer or order number"
                  className="w-full rounded-[12px] border border-[#f2c6cc] py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
                />
              </div>
              <select className="rounded-[12px] border border-[#f2c6cc] px-3 py-2 text-sm outline-none focus:border-[#d86a78]" value={usageStatus} onChange={(e) => setUsageStatus(e.target.value)}>
                <option value="">All usage statuses</option>
                <option value="REDEEMED">Redeemed</option>
                <option value="RESERVED">Reserved</option>
                <option value="RELEASED">Released</option>
              </select>
              <Button variant="dark" onClick={applyUsageFilters}>Apply</Button>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Order</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Used at</th>
                  </tr>
                </thead>
                <tbody>
                  {usageItems.map((usage) => (
                    <tr key={usage.id} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-3">
                        <div className="font-bold text-slate-950">{usage.userName || "Unknown user"}</div>
                        {usage.userPhone ? <div className="text-xs text-slate-500">{usage.userPhone}</div> : null}
                        {usage.userEmail ? <div className="text-xs text-slate-500">{usage.userEmail}</div> : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-bold text-slate-950">{usage.orderNumber || "—"}</div>
                        <div className="text-xs text-slate-500">Items {usage.itemCount} · Qty {usage.totalQty}</div>
                        {usage.addressText ? <div className="mt-1 max-w-[360px] text-xs text-slate-500">{usage.addressText}</div> : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-xs text-slate-500">Subtotal {money(usage.subtotal)}</div>
                        <div className="text-xs font-bold text-emerald-700">Coupon -{money(usage.couponDiscountAmount)}</div>
                        <div className="font-black text-slate-950">Paid {money(usage.grandTotal)}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-bold uppercase ${usageStatusClass(usage.status)}`}>{usage.status || "—"}</span>
                        <div className="mt-2 text-xs text-slate-500">Order: {usage.orderStatus || "—"}</div>
                        <div className="text-xs text-slate-500">Payment: {usage.paymentStatus || "—"}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{fmtDate(usage.usedAt)}</td>
                    </tr>
                  ))}
                  {!usageItems.length ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                        {usageLoading ? "Loading usage details..." : "No usage details found."}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {usageMeta.total > usageMeta.limit ? (
              <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-500">
                <Button size="sm" variant="ghost" disabled={usageLoading || usageMeta.page <= 1} onClick={() => loadCouponUsage(item, usageMeta.page - 1)}>Prev</Button>
                <span>Page {usageMeta.page} of {totalPages}</span>
                <Button size="sm" variant="ghost" disabled={usageLoading || usageMeta.page >= totalPages} onClick={() => loadCouponUsage(item, usageMeta.page + 1)}>Next</Button>
              </div>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        // eyebrow="Food coupons"
        title="Restaurant Coupons"
        subtitle="Admin-created restaurant coupons are approved immediately. Restaurant-created coupons stay pending until admin approval."
        icon={<Tag size={14} />}
      />

      {message ? <div className="rounded-[14px] border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-700">{message}</div> : null}
      {error ? <ErrorBox error={error} onRetry={load} /> : null}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-slate-950">Create coupon</h2>
              <p className="text-sm text-slate-500">Choose All restaurants or one restaurant by name.</p>
            </div>
            <Button type="submit" loading={saving}>{saving ? "Saving..." : "Create approved coupon"}</Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input label="Coupon code" value={form.code} onChange={(e) => patchForm("code", String(e.target.value || "").toUpperCase())} placeholder="FOOD20" required />
            <Input label="Title" value={form.title} onChange={(e) => patchForm("title", e.target.value)} placeholder="20% off" required />
            <Select label="Restaurant scope" value={form.restaurantId} onChange={(e) => patchForm("restaurantId", e.target.value)}>
              <option value="">All restaurants</option>
              {restaurants.map((row) => {
                const id = row.id || row.restaurantId || row.restaurant_id;
                const name = row.name || row.restaurantName || row.restaurant_name || row.businessName || row.business_name || row.legalName || row.legal_name || "Restaurant";
                return <option key={id} value={id}>{name}</option>;
              })}
            </Select>
            <Select label="Discount type" value={form.discountType} onChange={(e) => patchForm("discountType", e.target.value)}>
              <option value="PERCENT">Percent</option>
              <option value="FIXED">Fixed amount</option>
            </Select>
            <Input label="Discount value" type="number" min="0" step="0.01" value={form.discountValue} onChange={(e) => patchForm("discountValue", e.target.value)} required />
            <Input label="Max discount amount" type="number" min="0" step="0.01" value={form.maxDiscountAmount} onChange={(e) => patchForm("maxDiscountAmount", e.target.value)} disabled={form.discountType === "FIXED"} placeholder={form.discountType === "FIXED" ? "Not needed" : "100"} />
            <Input label="Min order amount" type="number" min="0" step="0.01" value={form.minOrderAmount} onChange={(e) => patchForm("minOrderAmount", e.target.value)} />
            <Input label="Total usage limit" type="number" min="1" step="1" value={form.usageLimit} onChange={(e) => patchForm("usageLimit", e.target.value)} placeholder="Blank = unlimited" />
            <Input label="Per user limit" type="number" min="1" step="1" value={form.perUserLimit} onChange={(e) => patchForm("perUserLimit", e.target.value)} />
            <Input label="Starts at" type="datetime-local" value={form.startsAt} onChange={(e) => patchForm("startsAt", e.target.value)} />
            <Input label="Ends at" type="datetime-local" value={form.endsAt} onChange={(e) => patchForm("endsAt", e.target.value)} />
            <div className="flex items-end gap-3 pb-2"><Switch checked={form.isActive} onChange={(v) => patchForm("isActive", v)} /><span className="text-sm font-bold text-slate-600">Active</span></div>
          </div>
          <Textarea label="Description" value={form.description} onChange={(e) => patchForm("description", e.target.value)} placeholder="Optional coupon description" />

          <CouponConditionFields form={form} onChange={patchForm} dayOptions={DAY_OPTIONS} />
        </form>
      </Card>

      <Card>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-black text-slate-950">Coupon history</h2>
            <p className="text-sm text-slate-500">Click usage to see which users used a coupon.</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search coupon" className="rounded-[12px] border border-[#f2c6cc] px-3 py-2 text-sm outline-none focus:border-[#d86a78]" />
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="rounded-[12px] border border-[#f2c6cc] px-3 py-2 text-sm outline-none focus:border-[#d86a78]">
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <select value={filters.active} onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))} className="rounded-[12px] border border-[#f2c6cc] px-3 py-2 text-sm outline-none focus:border-[#d86a78]">
              <option value="">Active + Inactive</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center text-slate-500"><Loader2 className="mr-2 animate-spin" size={18} /> Loading coupons...</div>
        ) : items.length ? (
          <TableShell columns={["Coupon", "Restaurant", "Discount", "Min order", "Rules", "Status", "Used", "Actions"]} minWidth={1050}>
            {items.map((item) => {
              const restaurantName = safeRestaurantLabel(item, restaurantNameById);
              return (
                <React.Fragment key={item.id}>
                  <tr className={`border-b border-slate-100 align-top hover:bg-[#fffafb] ${usageCoupon?.id === item.id ? "bg-[#fffafb]" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-black text-slate-950">{item.code}</div>
                      <div className="text-xs text-slate-500">{item.title}</div>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-700">{restaurantName}</td>
                    <td className="px-4 py-3"><div className="font-bold text-slate-900">{couponLabel(item)}</div></td>
                    <td className="px-4 py-3 font-bold text-slate-800">{money(item.minOrderAmount)}</td>
                    <td className="max-w-[280px] px-4 py-3 text-xs font-semibold text-slate-600">{formatRules(item)}</td>
                    <td className="px-4 py-3 space-y-1"><StatusPill value={item.approvalStatus} /><br /><StatusPill value={item.isActive ? "ACTIVE" : "INACTIVE"} /></td>
                    <td className="px-4 py-3"><button type="button" onClick={() => toggleUsage(item)} className="inline-flex items-center gap-1 rounded-lg bg-[#fff6f7] px-3 py-1.5 text-xs font-bold text-[#d86a78]"><Eye size={14} /> {item.usedCount || 0}</button></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {item.approvalStatus === "PENDING" ? <Button size="sm" onClick={() => runAction(() => restaurantAdminService.approveRestaurantCoupon(item.id), "Coupon approved.")}><CheckCircle2 size={14} /> Approve</Button> : null}
                        {item.approvalStatus === "PENDING" ? <Button size="sm" variant="danger" onClick={() => handleReject(item.id)}><XCircle size={14} /> Reject</Button> : null}
                        <Button size="sm" variant="ghost" onClick={() => runAction(() => restaurantAdminService.setRestaurantCouponActive(item.id, !item.isActive), item.isActive ? "Coupon deactivated." : "Coupon activated.")}>{item.isActive ? "Deactivate" : "Activate"}</Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(item)}><Trash2 size={14} /> Delete</Button>
                      </div>
                    </td>
                  </tr>
                  {renderUsagePanel(item)}
                </React.Fragment>
              );
            })}
          </TableShell>
        ) : (
          <EmptyState title="No restaurant coupons" subtitle="Create your first food coupon above." icon={<Tag size={20} />} />
        )}
      </Card>
    </div>
  );
}
